import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import { useTranslation } from 'react-i18next';
import { GeocodeResult } from '../../types/companyLocation';
import { reverseGeocodeWithGoogleMaps } from '../../utils/googleMapsGeocoding';
import { MapCenterPin } from './MapCenterPin';

const FALLBACK_GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
const DEFAULT_CENTER = { lat: 41.3275, lng: 19.8189 };

const mapContainerStyle = {
  width: '100%',
  height: '280px',
};

interface LocationMapInlineEditorProps {
  apiKey?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  onApply: (result: GeocodeResult) => void;
}

export const LocationMapInlineEditor: React.FC<LocationMapInlineEditorProps> = ({
  apiKey,
  latitude,
  longitude,
  onApply,
}) => {
  const { t } = useTranslation();
  const googleMapsApiKey = apiKey || FALLBACK_GOOGLE_MAPS_API_KEY;
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'rentman-google-maps-script',
    googleMapsApiKey,
  });

  const initialPosition = useMemo(() => {
    if (typeof latitude === 'number' && typeof longitude === 'number') {
      return { lat: latitude, lng: longitude };
    }

    return DEFAULT_CENTER;
  }, [latitude, longitude]);

  const [markerPosition, setMarkerPosition] = useState(initialPosition);
  const [isResolving, setIsResolving] = useState(false);
  const [resolveError, setResolveError] = useState<string | null>(null);
  const [resolvedResult, setResolvedResult] = useState<GeocodeResult | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);

  useEffect(() => {
    setMarkerPosition(initialPosition);
    setResolveError(null);
    setResolvedResult(null);
  }, [initialPosition]);

  const handleMapLoad = (map: google.maps.Map) => {
    mapRef.current = map;
  };

  const handleMapClick = (event: google.maps.MapMouseEvent | google.maps.IconMouseEvent) => {
    if (!event.latLng) {
      return;
    }

    // Clicking directly on a POI icon (a shop, landmark, etc.) fires an IconMouseEvent
    // carrying a placeId, and by default Google pops its own info card for that place.
    // Stop that default so the icon behaves like any other point on the map, and still
    // drop our pin at the coordinates that were clicked.
    if ('placeId' in event && event.placeId) {
      event.stop();
    }

    setMarkerPosition({ lat: event.latLng.lat(), lng: event.latLng.lng() });
    setResolvedResult(null);
    setResolveError(null);
  };

  // The pin overlay stays fixed at the container's visual center; dragging the map moves
  // the map underneath it instead of dragging the pin itself. Read the map's new center
  // once the drag settles and treat that as the newly selected point.
  const handleMapDragEnd = () => {
    const map = mapRef.current;
    if (!map) {
      return;
    }

    const center = map.getCenter();
    if (!center) {
      return;
    }

    setMarkerPosition({ lat: center.lat(), lng: center.lng() });
    setResolvedResult(null);
    setResolveError(null);
  };

  const handleResolveLocation = async () => {
    setIsResolving(true);
    setResolveError(null);

    try {
      const result = await reverseGeocodeWithGoogleMaps(markerPosition.lat, markerPosition.lng);
      setResolvedResult(result);
    } catch (error: any) {
      setResolveError(
        error.response?.data?.error ||
          error.message ||
          t('locations.mapPicker.geocodeFailed', {
            defaultValue: 'Could not resolve an address for this location.',
          })
      );
    } finally {
      setIsResolving(false);
    }
  };

  return (
    <Stack spacing={1}>
      {!googleMapsApiKey && (
        <Alert severity="warning">
          {t('locations.mapPicker.missingApiKey', {
            defaultValue: 'Google Maps API key is not configured. The map cannot be displayed.',
          })}
        </Alert>
      )}

      {loadError && (
        <Alert severity="error">
          {t('locations.mapPicker.loadError', { defaultValue: 'Failed to load Google Maps.' })}
        </Alert>
      )}

      {googleMapsApiKey && !isLoaded && !loadError && (
        <Box
          sx={{
            height: mapContainerStyle.height,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress size={24} />
        </Box>
      )}

      {googleMapsApiKey && isLoaded && (
        <Box
          sx={{
            position: 'relative',
            borderRadius: 1,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={markerPosition}
            zoom={15}
            onClick={handleMapClick}
            onLoad={handleMapLoad}
            onDragEnd={handleMapDragEnd}
            options={{
              streetViewControl: false,
              mapTypeControl: false,
              fullscreenControl: false,
              cameraControl: false,
              zoomControl: true,
            }}
          />
          <MapCenterPin />
        </Box>
      )}

      {resolveError && <Alert severity="error">{resolveError}</Alert>}

      {resolvedResult && (
        <Alert severity="success">
          <Stack spacing={0.5}>
            <Typography variant="body2">
              {[resolvedResult.address, resolvedResult.city]
                .filter(Boolean)
                .join(', ')}
            </Typography>
            {resolvedResult.plusCode && (
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {t('locations.plusCode', { defaultValue: 'Plus Code' })}: {resolvedResult.plusCode}
              </Typography>
            )}
          </Stack>
        </Alert>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <Button
          size="small"
          variant="outlined"
          onClick={handleResolveLocation}
          disabled={!googleMapsApiKey || !isLoaded || isResolving}
          sx={{ textTransform: 'none' }}
        >
          {isResolving
            ? t('locations.mapPicker.resolving', { defaultValue: 'Resolving...' })
            : t('locations.mapPicker.resolveAddress', { defaultValue: 'Resolve address' })}
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={() => resolvedResult && onApply(resolvedResult)}
          disabled={!resolvedResult}
          sx={{ textTransform: 'none' }}
        >
          {t('locations.mapInlineApply', { defaultValue: 'Apply to location' })}
        </Button>
      </Stack>
    </Stack>
  );
};
